import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { AnswerFile } from '../../models/QAndA';
import dataRoomApi from '../../dataRoomApi';
import MessageFile from 'components/MessageFiles/components/MessageFile';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';

import './AnswerFiles.styles.scss';

type AnswerFilesProps = {
  className?: string;
  answerId: string;
  files: AnswerFile[];
  updateAnswer: (files: AnswerFile[]) => void;
};

const AnswerFiles: React.FC<AnswerFilesProps> = (props) => {
  const { className, files, answerId, updateAnswer } = props;

  const {
    dataRoom: { _id: dataRoomId, name: dataRoomName, userId },
    qaSide,
  } = useContext(DataRoomContext);

  const classes = classNames('answer-files', className);

  const onFilePreview = useCallback(
    (fileId: string) =>
      dataRoomApi.questionsAndAnswers.previewAnswerFile(answerId, fileId),
    [answerId],
  );

  const onFileRemove = useCallback(
    async (fileId: string, removeFromDataRoom?: boolean) => {
      await dataRoomApi.questionsAndAnswers.removeAnswerFile(
        answerId,
        fileId,
        removeFromDataRoom,
      );
      updateAnswer(files.filter((el) => el._id !== fileId));
    },
    [files, answerId, updateAnswer],
  );

  const onFileAddToDataRoom = useCallback(
    async (fileId: string) => {
      await dataRoomApi.questionsAndAnswers.moveAnswerFileToDataRoom(
        answerId,
        fileId,
      );
      updateAnswer(
        files.map((el) =>
          el._id === fileId ? { ...el, isDataRoomFile: true } : el,
        ),
      );
    },
    [files, answerId, updateAnswer],
  );

  if (!files?.length) return null;

  return (
    <div className={classes}>
      {files.map(
        ({
          _id,
          isDataRoomFile,
          canMoveFileToDataRoom,
          file: { name, type, url, originalType },
        }) => (
          <MessageFile
            key={_id}
            id={_id}
            url={url}
            name={name}
            dataRoomId={dataRoomId}
            dataRoomName={dataRoomName}
            ownerId={userId}
            userSide={qaSide}
            type={originalType || type}
            isDataRoomFile={isDataRoomFile}
            canMoveFileToDataRoom={canMoveFileToDataRoom}
            canRemoveFile={canMoveFileToDataRoom}
            onFilePreview={onFilePreview}
            onFileAddToDataRoom={onFileAddToDataRoom}
            onFileRemove={onFileRemove}
          />
        ),
      )}
    </div>
  );
};

export default AnswerFiles;
